// Framed pictures in the hang slots (docs/contracts.md section 8). The room's art state (slot -> picture) comes
// from the server; an empty slot draws nothing. Each slot kind has three frames (slots.ts: portrait, landscape,
// square); a picture hangs in the one nearest its own aspect, centred on the slot, and is cover-cropped (centred) to
// that frame's picture aspect with a texture transform. The mat (entry.mat) is a colour, or "none": the picture then
// fills the frame opening.
//
// The pictures are not in the bake, so each frame takes its light from the wall behind it: once the room is in
// the scene, every slot's wall rectangle is rendered once from the front with an orthographic camera (grade at
// neutral, nothing hung yet) into a small target, read back and divided by the wall paint's albedo. That light,
// bilinear over a 5 x 5 grid, goes into the vertex colours of the frame, mat and picture. The room variables then
// apply through the same grade patch as the room materials (RoomShading.patch), and the contact shadow multiplies
// the wall's own (graded) colour, so everything follows the panel.
//
// A change to one slot builds that slot's frame only (its geometry, its texture); the frames of all slots are
// kept per slot and concatenated into the shared meshes. Draw calls: 1 (white / black frames, mats, gaps) + 1 (oak
// frames) + 1 (contact shadows) + 1 per picture. Nothing runs per frame.
//
// Animated pictures (GIF, animated WebP: entry.frames > 1) go through anim.ts (decoded once, one shared timer, only
// while in view). Small square images (tile.ts) repeat across the picture opening with nearest filtering.

import {
  AddEquation,
  ClampToEdgeWrapping,
  CustomBlending,
  DataTexture,
  Group,
  LinearFilter,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  NearestFilter,
  NearestMipmapNearestFilter,
  OrthographicCamera,
  RepeatWrapping,
  RGBAFormat,
  SRGBColorSpace,
  SrcColorFactor,
  type BufferGeometry,
  type Material,
  type Texture,
  TextureLoader,
  UnsignedByteType,
  WebGLRenderTarget,
  type Scene,
  type WebGLRenderer,
  ZeroFactor,
} from 'three';
import type { ArtEntry, ArtState } from '@lr3d/shared';
import type { RoomShading } from '../render/room-shading';
import { estimateTextureBytes } from '../render/memory';
import { ART_MAT_NONE } from '@lr3d/shared';
import { buildFrame, buildPicture, GeoBuilder, hexToLinear, softTint, type Finish, type FrameTargets, type Placement, type TintFn } from './frame';
import { cropRect, frameFor, type FrameFit, type Slot, type SlotSet } from './slots';
import { ArtAnimator, type AnimLoad } from './anim';
import { isTile, tileLayout } from './tile';
import { uvOrientation } from '../video/posters';

/** The window view's frame kind (slot file `frames.view`). */
export const VIEW_KIND = 'view';
/** The gallery's gold oval (slot file `frames.oval`). */
export const OVAL_KIND = 'oval';
/**
 * Surface slots: frame kinds whose picture goes on a scene mesh (manifest screen node) instead of into a frame.
 * Tile size: TILE_PX_M-style metres per image pixel, clamped.
 */
export const SURFACES: Record<string, { node: string; fit: 'region' | 'cover'; tilePxM: number; tileMin: number; tileMax: number }> = {
  [VIEW_KIND]: { node: 'window_view', fit: 'region', tilePxM: 0.012, tileMin: 0.4, tileMax: 1.2 },
  [OVAL_KIND]: { node: 'oval_screen', fit: 'cover', tilePxM: 0.002, tileMin: 0.06, tileMax: 0.16 },
};
/**
 * The Basement's overlay (2026-10-30, docs/plans/basement-overlay.md): a slot whose picture is not drawn in the scene
 * but over the whole view (overlay/overlay.ts). Artworks keeps its entry (the panel reads it) and hands it on through
 * onOutside; the overlay reports what it shows through outsideShown.
 */
export const OVERLAY_KIND = 'overlay';
/** Slot kinds without a frame (the panel hides the frame colour and mat): the surface slots and the overlay. */
export const frameless = (kind: string): boolean => !!SURFACES[kind] || kind === OVERLAY_KIND;
/** The window view image's size on the plane past the windows (m) and its centre height (the windows' middle, m). */
const VIEW_IMAGE_M: [number, number] = [8, 4];
const VIEW_CENTRE_Z = 1.35;

/** Wall paint albedo (linear), cad lib/materials/library.py WHITE (Oxford White). */
const WALL_ALBEDO = [0.8, 0.785, 0.75];
const GRID = 5;
const SUPER = 4; // rendered pixels per grid cell (box-averaged)
/** Oak grain map values are in 0.8..1; the vertex albedo is raised to keep the oak's mean colour. */
const OAK_GAIN = 1 / 0.9;

interface Hung {
  entry: ArtEntry;
  /** The file this texture was loaded from (entry.hash can move ahead while the next file loads). */
  hash: string;
  texture: Texture;
  targets: FrameTargets;
  picture: Mesh;
  anim?: AnimLoad;
}

export class Artworks {
  readonly group = new Group();
  readonly stats = { frames: 0, triangles: 0, meshes: 0, sampleMs: 0, buildMs: 0, textureBytes: 0, loads: 0 };
  private hung = new Map<string, Hung>();
  /** Slot -> the file whose image failed to load (the wall keeps showing what it had). */
  private failed = new Map<string, string>();
  /** The art state as last applied (slot -> entry), including pictures still loading. */
  private wanted = new Map<string, ArtEntry>();
  private version = new Map<string, number>();
  private tints = new Map<string, TintFn>();
  private loader = new TextureLoader();
  private frameMat: MeshBasicMaterial;
  private oakMat: MeshBasicMaterial;
  private shadowMat: MeshBasicMaterial;
  private shared: Texture[] = [];
  private merged: Mesh[] = [];
  private aniso: number;
  /** dispose() ran (a room switch): loads that finish later are released, not hung. */
  private disposed = false;
  /** Animated pictures: decoding, memory budget, the shared timer. */
  readonly anim: ArtAnimator;
  /** A slot's picture appeared, changed or went (after its texture loaded). */
  onChange: (slot: string) => void = () => {};

  constructor(private renderer: WebGLRenderer, private scene: Scene, private shading: RoomShading, readonly slots: SlotSet,
              private fileUrl: (e: ArtEntry) => string) {
    this.group.name = 'artworks';
    this.group.matrixAutoUpdate = false;
    this.aniso = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
    this.anim = new ArtAnimator(renderer);
    this.frameMat = this.graded(new MeshBasicMaterial({ vertexColors: true, name: 'art_frame' }));
    const grain = oakGrain();
    grain.anisotropy = this.aniso;
    this.oakMat = this.graded(new MeshBasicMaterial({ vertexColors: true, map: grain, name: 'art_frame_oak' }));
    // contact shadows: multiply the wall (dst * src), drawn after the opaque room, not graded (the wall already is)
    const shadowTex = shadowTexture();
    this.shared.push(grain, shadowTex);
    this.shadowMat = new MeshBasicMaterial({
      map: shadowTex,
      name: 'art_shadow',
      transparent: true,
      depthWrite: false,
      blending: CustomBlending,
      blendEquation: AddEquation,
      blendSrc: ZeroFactor,
      blendDst: SrcColorFactor,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
    });
  }

  /**
   * Sample the wall light behind every slot (once; call when the room is in the scene and nothing is hung) and add
   * the group to the scene.
   */
  init(): void {
    const t0 = performance.now();
    const list = [...this.slots.slots.values()].filter((s) => s.kind !== OVERLAY_KIND);
    // the light over the largest rectangle any of the slot's frames takes
    const tints = this.shading.neutral(() => sampleWalls(this.renderer, this.scene, list.map((s) => ({ pl: s.pl, w: s.sampleW, h: s.sampleH }))));
    list.forEach((s, i) => this.tints.set(s.id, tints[i]!));
    this.stats.sampleMs = performance.now() - t0;
    this.group.updateMatrixWorld(true);
    this.scene.add(this.group);
  }

  /** Tier (canvas sizes, memory budget, frame rate cap) and the render request for animation frames. */
  animate(o: { mobile: boolean; invalidate: () => void }): void {
    this.anim.configure(o);
  }

  has(slot: string): boolean {
    return this.wanted.has(slot);
  }

  entry(slot: string): ArtEntry | undefined {
    return this.wanted.get(slot);
  }

  /** The file the wall shows in this slot now (undefined while empty; the old file while the next one loads). */
  shown(slot: string): string | undefined {
    return this.hung.get(slot)?.hash ?? this.surfaces.get(slot)?.hash ?? this.outside.get(slot);
  }

  /** Slot (OVERLAY_KIND) -> the file the overlay shows now. */
  private outside = new Map<string, string>();
  /** The wanted picture of a slot drawn outside the scene (the overlay) changed: load and show it (null = none). */
  onOutside: (slot: string, entry: ArtEntry | null) => void = () => {};

  /** What the overlay shows in `slot` now (undefined = nothing), or that `failed` (a file hash) did not load. */
  outsideShown(slot: string, hash: string | undefined, failed?: string): void {
    if (failed) this.failed.set(slot, failed);
    else if (hash) { this.outside.set(slot, hash); this.failed.delete(slot); }
    else this.outside.delete(slot);
    this.onChange(slot);
  }

  /** True when the slot's wanted image failed to load. */
  loadFailed(slot: string): boolean {
    const w = this.wanted.get(slot);
    return !!w && this.failed.get(slot) === w.hash;
  }

  /** Load the slot's wanted image again (after a failure, or when the wall does not show it). */
  retry(slot: string): void {
    const e = this.wanted.get(slot);
    if (!e) return;
    this.wanted.delete(slot);
    this.set(slot, e);
  }

  /** The room's complete art state (welcome, or GET /art/<room>): slots missing from it are emptied. */
  applyState(state: ArtState): void {
    for (const id of [...this.wanted.keys()]) if (!state[id]) this.set(id, null);
    for (const [id, e] of Object.entries(state)) this.set(id, e);
  }

  /**
   * One slot's picture (null = empty). Same file as now: only the frame is rebuilt if its finish changed (the
   * picture and its texture stay).
   */
  set(id: string, entry: ArtEntry | null): void {
    const slot = this.slots.get(id);
    if (!slot) return;
    if (SURFACES[slot.kind]) { this.setSurface(slot, entry); return; }
    const cur = this.wanted.get(id);
    if (slot.kind === OVERLAY_KIND) {
      if (entry) this.wanted.set(id, entry); else { this.wanted.delete(id); this.failed.delete(id); }
      if (cur?.hash !== entry?.hash) this.onOutside(id, entry);
      this.onChange(id);
      return;
    }
    if (entry && cur && cur.hash === entry.hash) {
      this.wanted.set(id, entry); // same file (title, uploader or finish may differ)
      const h = this.hung.get(id);
      if (!h) return; // still loading: hang() reads the wanted entry
      const changed = frameKey(slot, h.entry) !== frameKey(slot, entry);
      const resized = (h.entry.mat === ART_MAT_NONE) !== (entry.mat === ART_MAT_NONE);
      h.entry = entry;
      if (changed) {
        const { targets, z } = this.frameTargets(slot, entry);
        h.targets = targets;
        // with or without a mat the picture's size changes: its quad and crop follow (the texture stays)
        if (resized) this.placePicture(slot, entry, h.texture, h.picture, z, !!h.anim);
        this.rebuild();
        this.onChange(id);
      }
      return;
    }
    if (!entry && !cur) return;
    const v = (this.version.get(id) ?? 0) + 1;
    this.version.set(id, v);
    if (!entry) {
      this.wanted.delete(id);
      this.unhang(id);
      this.rebuild();
      this.onChange(id);
      return;
    }
    this.wanted.set(id, entry);
    this.failed.delete(id);
    this.stats.loads++;
    const url = this.fileUrl(entry);
    const tile = isTile(entry.w, entry.h);
    const load: Promise<{ tex: Texture; anim?: AnimLoad }> = (entry.frames ?? 1) > 1
      ? this.anim.load(slot, entry, url).then((a) => ({ tex: a.texture, anim: a }))
      : this.loader.loadAsync(url).then((tex) => { if (!tile) fitToFrame(tex, slot); return { tex }; });
    load.then(({ tex, anim }) => {
      if (this.disposed || this.version.get(id) !== v) { anim?.release(); tex.dispose(); return; }
      this.unhang(id);
      this.hang(slot, this.wanted.get(id) ?? entry, tex, anim);
      this.rebuild();
      this.onChange(id);
    }).catch((e) => {
      if (this.disposed) return;
      console.warn(`[art] ${id}: image failed (${url})`, e);
      if (this.version.get(id) !== v) return;
      this.failed.set(id, entry.hash);
      this.onChange(id);
    });
  }

  /**
   * The window view (frame kind `view`, 2026-10-25): no frame; the image goes on the scene's `window_view` plane past
   * the windows. A tile (tile.ts: a small square image) repeats over the plane with sharp pixels, VIEW_TILE_PX_M per
   * image pixel; any other image covers the whole plane once (cover crop, centred), so the windows show its middle.
   */
  /** Surface slots with a picture on them: slot id -> the mesh, its material before, ours, the texture. */
  private surfaces = new Map<string, { mesh: Mesh; orig: Material | Material[]; mat: MeshBasicMaterial; tex: Texture | null; hash?: string; anim?: AnimLoad }>();
  /**
   * Called just before a surface slot's mesh gets a picture (hung = true) and just after it gives it back (false):
   * the app moves the mesh out of / back into the poster screens (the oval shows the music poster while empty).
   */
  onSurface: (kind: string, mesh: Mesh, hung: boolean) => void = () => {};

  /** The scene's surface meshes were replaced (a tier upgrade): put the wanted images on the new ones. */
  reapplySurfaces(): void {
    for (const slot of this.slots.slots.values()) {
      if (!SURFACES[slot.kind]) continue;
      const e = this.wanted.get(slot.id);
      const cur = this.surfaces.get(slot.id);
      if (cur) { cur.tex?.dispose(); cur.anim?.release(); this.surfaces.delete(slot.id); }
      if (!e) continue;
      this.wanted.delete(slot.id);
      this.setSurface(slot, e);
    }
  }

  private surfaceMesh(kind: string): Mesh | undefined {
    let mesh: Mesh | undefined;
    const node = SURFACES[kind]!.node;
    this.scene.traverse((o) => { if (!mesh && (o as Mesh).isMesh && o.userData.screenNode === node) mesh = o as Mesh; });
    return mesh;
  }

  private dropSurface(slot: Slot): void {
    const cur = this.surfaces.get(slot.id);
    if (!cur) return;
    cur.mesh.material = cur.orig;
    cur.tex?.dispose(); cur.anim?.release(); cur.mat.dispose();
    this.surfaces.delete(slot.id);
    this.onSurface(slot.kind, cur.mesh, false);
  }

  /**
   * A surface slot (SURFACES: the window view, the gallery's oval): no frame; the picture goes on a scene mesh of
   * its own. Fit per kind: "region" (the window view: cover-fit to a region of the plane, VIEW_IMAGE_M), "cover" (the
   * oval: cover-fit to the screen's bounding rectangle, so it fills the whole oval inside it). A tile (tile.ts) repeats
   * with sharp pixels. Animated pictures animate (anim.ts).
   */
  private setSurface(slot: Slot, entry: ArtEntry | null): void {
    const spec = SURFACES[slot.kind]!;
    // the same file again (the server's broadcast after our own hang): keep the load that is running or done. A new
    // version here would drop that load's result, and the surface would keep the previous picture.
    if (entry && this.wanted.get(slot.id)?.hash === entry.hash) { this.wanted.set(slot.id, entry); return; }
    const v = (this.version.get(slot.id) ?? 0) + 1;
    this.version.set(slot.id, v);
    if (!entry) {
      this.wanted.delete(slot.id);
      this.dropSurface(slot);
      this.onChange(slot.id);
      return;
    }
    this.wanted.set(slot.id, entry);
    this.failed.delete(slot.id);
    const mesh = this.surfaceMesh(slot.kind);
    if (!mesh) return;
    const url = this.fileUrl(entry);
    const tile = isTile(entry.w, entry.h);
    const load: Promise<{ tex: Texture; anim?: AnimLoad }> = (entry.frames ?? 1) > 1
      ? this.anim.load(slot, entry, url).then((a) => ({ tex: a.texture, anim: a }))
      : this.loader.loadAsync(url).then((tex) => ({ tex }));
    load.then(({ tex, anim }) => {
      if (this.disposed || this.version.get(slot.id) !== v) { anim?.release(); tex.dispose(); return; }
      tex.colorSpace = SRGBColorSpace;
      mesh.geometry.computeBoundingBox();
      const b = mesh.geometry.boundingBox!.clone().applyMatrix4(mesh.matrixWorld);
      const pw = Math.hypot(b.max.x - b.min.x, b.max.z - b.min.z), ph = b.max.y - b.min.y;
      const o = uvOrientation(mesh);
      const img = tex.image as { width: number; height: number };
      const ia = (img?.width || entry.w) / (img?.height || entry.h);
      if (tile) {
        const side = Math.min(spec.tileMax, Math.max(spec.tileMin, entry.w * spec.tilePxM));
        tex.wrapS = tex.wrapT = RepeatWrapping;
        tex.magFilter = NearestFilter;
        tex.minFilter = NearestMipmapNearestFilter;
        tex.repeat.set(pw / side, ph / side);
        tex.offset.set(0.5 - 0.5 * pw / side, 0.5 - 0.5 * ph / side);   // a tile centred on the surface
      } else {
        // the image covers a rectangle (cover crop, centred): the region (window view) or the whole surface (oval)
        const [rw, rh] = spec.fit === 'region' ? VIEW_IMAGE_M : [pw, ph];
        const ra = rw / rh;
        const cx = ia > ra ? ra / ia : 1, cy = ia > ra ? 1 : ia / ra;          // the part of the image used
        const su = rw / pw, sv = rh / ph;                                      // region size in surface UV
        const u0 = 0.5 - su / 2, v0 = spec.fit === 'region' ? (VIEW_CENTRE_Z - b.min.y) / ph - sv / 2 : 0.5 - sv / 2;
        tex.wrapS = tex.wrapT = ClampToEdgeWrapping;
        tex.anisotropy = this.aniso;
        tex.repeat.set(cx / su, cy / sv);
        tex.offset.set(-u0 * cx / su + (1 - cx) / 2, -v0 * cy / sv + (1 - cy) / 2);
      }
      if (o.flipU) { tex.offset.x += tex.repeat.x; tex.repeat.x *= -1; }
      // image rows run top-down (TextureLoader flips them, a DataTexture does not)
      if (tex.flipY ? !o.flipV : o.flipV) { tex.offset.y += tex.repeat.y; tex.repeat.y *= -1; }
      tex.needsUpdate = true;
      let cur = this.surfaces.get(slot.id);
      if (!cur) {
        this.onSurface(slot.kind, mesh, true);     // the poster lets go of the mesh first
        const mat = new MeshBasicMaterial({ name: `${slot.kind}_art`, toneMapped: false });
        cur = { mesh, orig: mesh.material, mat, tex: null };
        this.surfaces.set(slot.id, cur);
        mesh.material = mat;
      }
      cur.tex?.dispose(); cur.anim?.release();
      cur.tex = tex; cur.anim = anim; cur.hash = entry.hash;
      anim?.attach(mesh);
      cur.mat.map = tex;
      cur.mat.needsUpdate = true;
      this.onChange(slot.id);
    }).catch((e) => {
      if (this.disposed) return;
      console.warn(`[art] ${slot.id}: image failed (${url})`, e);
      if (this.version.get(slot.id) !== v) return;
      this.failed.set(slot.id, entry.hash);
      this.onChange(slot.id);
    });
  }

  /** The frame finish a picture hangs in: its own, else the slot's frame default. */
  finishOf(slot: Slot, entry?: ArtEntry): Finish {
    return finishOf(slot, entry);
  }

  /** The frame a slot's picture hangs in (its shape and sizes); the slot's default frame while it is empty. */
  frameOf(slot: Slot, entry?: ArtEntry): FrameFit {
    return frameFor(slot, entry);
  }

  /** The frame's box for the selection outline: outer size (of the frame the picture hangs in) and depth off the wall. */
  frameBox(id: string): { pl: Placement; w: number; h: number; back: number; front: number } | null {
    const s = this.slots.get(id);
    if (!s) return null;
    if (frameless(s.kind)) return { pl: s.pl, w: 2 * s.hw, h: 2 * s.hh, back: 0, front: 0.01 };
    const { layout } = frameFor(s, this.hung.get(id)?.entry ?? this.wanted.get(id));
    return { pl: s.pl, w: layout.outerW, h: layout.outerH, back: s.frame.hang, front: s.frame.hang + 0.025 };
  }

  private graded(m: MeshBasicMaterial): MeshBasicMaterial {
    this.shading.patch(m);
    return m;
  }

  /**
   * The frame of one slot in the picture's finish, in builders of its own (oak bars go to `oak`, white and black
   * ones to `plain`); returns them and the picture plane's distance from the wall.
   */
  private frameTargets(slot: Slot, entry: ArtEntry): { targets: FrameTargets; z: number } {
    const tint = this.tintOf(slot);
    const finish = this.finishOf(slot, entry);
    const oakTint: TintFn = (x, y, out) => { tint(x, y, out); out[0] *= OAK_GAIN; out[1] *= OAK_GAIN; out[2] *= OAK_GAIN; };
    const targets: FrameTargets = { plain: new GeoBuilder(), oak: new GeoBuilder(), shadow: new GeoBuilder() };
    // the white and black frames and the mat take a weaker version of the wall light (they read whiter than the
    // wall); the picture and oak take the full light
    const color = finish === 'custom' && entry.color ? hexToLinear(entry.color) : undefined;
    const matColor = entry.mat && entry.mat !== ART_MAT_NONE ? hexToLinear(entry.mat) : undefined;
    const { spec, layout } = frameFor(slot, entry);
    const { z } = buildFrame({ ...spec, finish, color, matColor }, layout, slot.pl, finish === 'oak' ? oakTint : softTint(tint), targets);
    return { targets, z };
  }

  private tintOf(slot: Slot): TintFn {
    return this.tints.get(slot.id) ?? ((_x: number, _y: number, out: [number, number, number]) => { out[0] = out[1] = out[2] = 1; });
  }

  private hang(slot: Slot, entry: ArtEntry, texture: Texture, anim?: AnimLoad): void {
    const t0 = performance.now();
    const { targets, z } = this.frameTargets(slot, entry);
    texture.colorSpace = SRGBColorSpace;
    texture.name = `art:${slot.id}`;
    texture.anisotropy = this.aniso;
    texture.wrapS = texture.wrapT = ClampToEdgeWrapping;
    const mat = this.graded(new MeshBasicMaterial({ vertexColors: true, map: texture, name: `art_picture:${slot.id}` }));
    const picture = new Mesh(undefined, mat);
    picture.name = `art:picture:${slot.id}`;
    picture.matrixAutoUpdate = false;
    this.placePicture(slot, entry, texture, picture, z, !!anim);
    anim?.attach(picture);
    this.group.add(picture);
    this.hung.set(slot.id, { entry, hash: entry.hash, texture, targets, picture, anim });
    this.stats.buildMs = performance.now() - t0;
  }

  /**
   * The picture's quad (the frame's picture size, at distance z from the wall) and the texture's crop: a cover crop
   * (centred) at the picture's aspect, or a tile's repeats. `anim`: the texture's rows run top first.
   */
  private placePicture(slot: Slot, entry: ArtEntry, texture: Texture, picture: Mesh, z: number, anim: boolean): void {
    const { layout } = frameFor(slot, entry);
    const img = texture.image as { width: number; height: number };
    if (isTile(entry.w, entry.h)) {
      // a small square image: repeated over the opening, whole tiles across, one centred vertically, pixels sharp
      const t = tileLayout(img.width, layout.imageW, layout.imageH);
      texture.wrapS = texture.wrapT = RepeatWrapping;
      texture.magFilter = NearestFilter;
      texture.minFilter = NearestMipmapNearestFilter;
      texture.anisotropy = 1;
      texture.repeat.set(t.rx, t.ry);
      texture.offset.set(t.ox, t.oy);
    } else {
      // cover crop, centred: show the middle part of the image at the picture's aspect
      const imgAspect = img.width / img.height;
      const picAspect = layout.imageW / layout.imageH;
      if (imgAspect > picAspect) { texture.repeat.set(picAspect / imgAspect, 1); texture.offset.set((1 - picAspect / imgAspect) / 2, 0); }
      else { texture.repeat.set(1, imgAspect / picAspect); texture.offset.set(0, (1 - imgAspect / picAspect) / 2); }
    }
    if (anim) {
      // animation frames are uploaded rows top first (DataTexture, no flip): mirror v
      texture.repeat.y = -texture.repeat.y;
      texture.offset.y = 1 - texture.offset.y;
    }
    const old = picture.geometry;
    picture.geometry = buildPicture(layout, slot.pl, z, this.tintOf(slot));
    old?.dispose();
  }

  private unhang(id: string): void {
    const h = this.hung.get(id);
    if (!h) return;
    h.picture.removeFromParent();
    h.picture.geometry.dispose();
    (h.picture.material as MeshBasicMaterial).dispose();
    h.anim?.release();
    h.texture.dispose();
    this.hung.delete(id);
  }

  /** Concatenate every hung frame into the shared frame / oak / shadow meshes. */
  private rebuild(): void {
    for (const m of this.merged) { m.removeFromParent(); m.geometry.dispose(); }
    this.merged = [];
    const all: FrameTargets = { plain: new GeoBuilder(), oak: new GeoBuilder(), shadow: new GeoBuilder() };
    for (const h of this.hung.values()) {
      all.plain.append(h.targets.plain);
      all.oak.append(h.targets.oak);
      all.shadow.append(h.targets.shadow);
    }
    const add = (geo: BufferGeometry | null, mat: MeshBasicMaterial, name: string, order = 0) => {
      if (!geo) return;
      const m = new Mesh(geo, mat);
      m.name = name;
      m.matrixAutoUpdate = false;
      m.renderOrder = order;
      this.group.add(m);
      this.merged.push(m);
    };
    add(all.plain.build(false), this.frameMat, 'art:frames');
    add(all.oak.build(true), this.oakMat, 'art:frames_oak');
    add(all.shadow.build(true), this.shadowMat, 'art:shadows', 1);
    let tris = 0, bytes = 0;
    for (const m of this.merged) tris += m.geometry.index!.count / 3;
    for (const h of this.hung.values()) { tris += h.picture.geometry.index!.count / 3; bytes += estimateTextureBytes(h.texture); }
    for (const t of this.shared) bytes += estimateTextureBytes(t);
    bytes += this.anim.stats.bytes; // decoded animation frames
    this.stats.frames = this.hung.size;
    this.stats.triangles = tris;
    this.stats.meshes = this.merged.length + this.hung.size;
    this.stats.textureBytes = this.hung.size ? bytes : 0;
  }

  /**
   * Free everything (a room switch). Surface slots give their scene mesh back (its own material, and the poster
   * screens through onSurface: with a shared build the next room shows the same mesh); loads still running are
   * released when they finish.
   */
  dispose(): void {
    this.disposed = true;
    for (const slot of this.slots.slots.values()) if (SURFACES[slot.kind]) this.dropSurface(slot);
    for (const id of [...this.hung.keys()]) this.unhang(id);
    for (const m of this.merged) m.geometry.dispose();
    for (const m of [this.frameMat, this.oakMat, this.shadowMat]) m.dispose();
    for (const t of this.shared) t.dispose();
    this.anim.dispose();
    this.group.removeFromParent();
  }
}

const finishOf = (slot: Slot, entry?: ArtEntry): Finish => entry?.finish ?? slot.spec.finish;

/**
 * Library images keep their own aspect and are up to the largest frame kind's max_px (contracts section 8), so one
 * file can hang in either frame kind. Before upload, crop the texture to the picture aspect of the frame it hangs in
 * (cover, centred; slots.ts cropRect, which also covers the frame without a mat) and scale it to the slot's max_px, so
 * a frame never holds more texture than it shows. An image that already fits is left as it is; placePicture() then
 * cover-crops with the texture transform.
 */
function fitToFrame(tex: Texture, slot: Slot): void {
  const img = tex.image as HTMLImageElement | undefined;
  const w = img?.naturalWidth || img?.width || 0, h = img?.naturalHeight || img?.height || 0;
  if (!img || !w || !h) return;
  const { sw, sh } = cropRect(slot, w, h);
  const k = Math.min(1, slot.frame.max_px / Math.max(sw, sh));
  if (sw > w * 0.98 && sh > h * 0.98 && k === 1) return;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(sw * k));
  c.height = Math.max(1, Math.round(sh * k));
  const ctx = c.getContext('2d');
  if (!ctx) return;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, (w - sw) / 2, (h - sh) / 2, sw, sh, 0, 0, c.width, c.height);
  tex.image = c;
  tex.needsUpdate = true;
}
/** What the frame's look depends on: the finish, the colour of a painted one, and the mat. */
const frameKey = (slot: Slot, entry: ArtEntry): string => `${finishOf(slot, entry)}${entry.finish === 'custom' ? entry.color : ''}|${entry.mat ?? ''}`;

/**
 * Render each wall rectangle (w x h, centred on its placement) from the front into its own square of one small
 * target (one read-back for all), and return the light there (baked colour / wall albedo) as a bilinear
 * function of the frame's local x, y.
 */
function sampleWalls(renderer: WebGLRenderer, scene: Scene, rects: { pl: Placement; w: number; h: number }[]): TintFn[] {
  const n = GRID * SUPER;
  const count = rects.length;
  if (!count) return [];
  // sRGB target: the room's programs render with the same output encoding as to the canvas, so nothing is
  // compiled for this pass (a linear target needs a second variant of every program: ~200 ms on load)
  const rt = new WebGLRenderTarget(n * count, n, { type: UnsignedByteType, format: RGBAFormat, depthBuffer: true, colorSpace: SRGBColorSpace });
  const buf = new Uint8Array(n * count * n * 4);
  const prev = renderer.getRenderTarget();
  try {
    renderer.setRenderTarget(rt);
    rects.forEach(({ pl, w, h }, i) => {
      const cam = new OrthographicCamera(-w / 2, w / 2, h / 2, -h / 2, 0.05, 0.6);
      cam.position.copy(pl.origin).addScaledVector(pl.normal, 0.3);
      cam.up.copy(pl.up);
      cam.lookAt(pl.origin);
      cam.updateMatrixWorld(true);
      rt.viewport.set(i * n, 0, n, n);
      rt.scissor.set(i * n, 0, n, n);
      rt.scissorTest = true;
      renderer.setRenderTarget(rt); // applies the viewport
      renderer.render(scene, cam);
    });
    renderer.readRenderTargetPixels(rt, 0, 0, n * count, n, buf);
  } finally {
    renderer.setRenderTarget(prev);
    rt.dispose();
  }
  return rects.map(({ w, h }, i) => tintFrom(buf, n * count, i * n, w, h));
}

/** The light grid of one sampled square (pixels x0.. of a row-major RGBA buffer `stride` wide). */
function tintFrom(buf: Uint8Array, stride: number, x0px: number, w: number, h: number): TintFn {
  // box-average SUPER x SUPER pixels per cell (row 0 = bottom = -y)
  const grid = new Float32Array(GRID * GRID * 3);
  for (let gy = 0; gy < GRID; gy++) for (let gx = 0; gx < GRID; gx++) {
    for (let c = 0; c < 3; c++) {
      let acc = 0;
      for (let sy = 0; sy < SUPER; sy++) for (let sx = 0; sx < SUPER; sx++) acc += srgbToLinear(buf[((gy * SUPER + sy) * stride + x0px + gx * SUPER + sx) * 4 + c]! / 255);
      grid[(gy * GRID + gx) * 3 + c] = Math.min(1.6, acc / (SUPER * SUPER) / WALL_ALBEDO[c]!);
    }
  }
  return (x, y, out) => {
    const fx = Math.min(GRID - 1, Math.max(0, (x / w + 0.5) * GRID - 0.5));
    const fy = Math.min(GRID - 1, Math.max(0, (y / h + 0.5) * GRID - 0.5));
    const x0 = Math.min(GRID - 2, Math.floor(fx)), y0 = Math.min(GRID - 2, Math.floor(fy));
    const tx = fx - x0, ty = fy - y0;
    for (let c = 0; c < 3; c++) {
      const g = (i: number, j: number) => grid[(j * GRID + i) * 3 + c]!;
      out[c] = (g(x0, y0) * (1 - tx) + g(x0 + 1, y0) * tx) * (1 - ty) + (g(x0, y0 + 1) * (1 - tx) + g(x0 + 1, y0 + 1) * tx) * ty;
    }
  };
}

const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));

const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/**
 * Contact shadow factor (linear, multiplied over the wall). u and v run 0 -> 0.5 from the shadow's outer edge to
 * its inner edge (the frame edge is at 0.25) and 0.5 -> 1 back out; darkness is the product of the two
 * directions, so the corners round off. A wide soft part plus a tighter one near the frame edge.
 */
function shadowTexture(): DataTexture {
  const N = 64;
  const STRENGTH = 0.6;
  const data = new Uint8Array(N * N * 4);
  const g = (u: number) => {
    const s = Math.min(u, 1 - u) * 2; // 0 outer edge, 0.5 frame edge, 1 inside
    return 0.55 * smooth(0, 1, s) + 0.45 * smooth(0.3, 0.62, s);
  };
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    const d = g((i + 0.5) / N) * g((j + 0.5) / N);
    const v = Math.round(255 * (1 - STRENGTH * d));
    const o = (j * N + i) * 4;
    data[o] = data[o + 1] = data[o + 2] = v;
    data[o + 3] = 255;
  }
  const t = new DataTexture(data, N, N, RGBAFormat, UnsignedByteType);
  t.wrapS = t.wrapT = ClampToEdgeWrapping;
  t.magFilter = LinearFilter;
  t.minFilter = LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  t.name = 'art:shadow';
  return t;
}

/** Oak grain: fine lines along the bar (u), a factor in 0.8..1 (linear), tiled. */
function oakGrain(): DataTexture {
  const W = 256, H = 64;
  const data = new Uint8Array(W * H * 4);
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const phase = Array.from({ length: 6 }, () => rnd() * 6.283);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    const u = i / W, v = j / H;
    const warp = 0.025 * Math.sin(6.283 * (u + phase[0]!)) + 0.012 * Math.sin(6.283 * (3 * u + phase[1]!));
    const lines = 0.5 + 0.5 * Math.sin(6.283 * 7 * (v + warp) + phase[2]!);
    const fine = 0.5 + 0.5 * Math.sin(6.283 * 23 * (v + 0.6 * warp) + phase[3]!);
    const f = 1 - 0.13 * Math.pow(lines, 3) - 0.05 * fine - 0.02 * rnd();
    const o = (j * W + i) * 4;
    data[o] = data[o + 1] = data[o + 2] = Math.round(255 * Math.max(0.8, Math.min(1, f)));
    data[o + 3] = 255;
  }
  const t = new DataTexture(data, W, H, RGBAFormat, UnsignedByteType);
  t.wrapS = t.wrapT = RepeatWrapping;
  t.magFilter = LinearFilter;
  t.minFilter = LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.needsUpdate = true;
  t.name = 'art:oak_grain';
  return t;
}
